import { useCallback } from 'react';
import { useLanguage } from './LanguageContext';
import { translations, Language, TranslationKeys } from './index';

// Helper type to get nested keys with dot notation
type PathsToStringProps<T> = T extends string
  ? []
  : {
      [K in Extract<keyof T, string>]: [K, ...PathsToStringProps<T[K]>];
    }[Extract<keyof T, string>];

type Join<T extends string[], D extends string> = T extends []
  ? never
  : T extends [infer F]
    ? F
    : T extends [infer F, ...infer R]
      ? F extends string
        ? `${F}${D}${Join<Extract<R, string[]>, D>}`
        : never
      : string;

// All valid translation keys as a union type
export type TranslationKey = Join<PathsToStringProps<TranslationKeys>, '.'>;

// Get value at path
function getNestedValue(obj: Record<string, unknown>, path: string): string {
  const keys = path.split('.');
  let current: unknown = obj;

  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = (current as Record<string, unknown>)[key];
    } else {
      // Return the key itself if not found (fallback)
      console.warn(`Translation key not found: ${path}`);
      return path;
    }
  }

  return typeof current === 'string' ? current : path;
}

// Interpolation: replace {{variable}} with values
function interpolate(text: string, params?: Record<string, string | number>): string {
  if (!params) return text;

  return text.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    return params[key]?.toString() ?? `{{${key}}}`;
  });
}

interface UseTranslationReturn {
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
  language: Language;
  setLanguage: (lang: Language) => void;
}

export function useTranslation(): UseTranslationReturn {
  const { language, setLanguage } = useLanguage();

  const t = useCallback(
    (key: TranslationKey, params?: Record<string, string | number>): string => {
      const translation = getNestedValue(
        translations[language] as unknown as Record<string, unknown>,
        key
      );
      return interpolate(translation, params);
    },
    [language]
  );

  return { t, language, setLanguage };
}
